import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

// All possible roles in the system
type AppRole = 'super_admin' | 'local_admin' | 'admin' | 'facilitator' | 'client';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles: AppRole[];
  redirectTo?: string;
}

// Role hierarchy: higher roles can access lower role features
const roleHierarchy: Record<AppRole, AppRole[]> = {
  'super_admin': ['super_admin', 'local_admin', 'admin', 'facilitator', 'client'],
  'local_admin': ['local_admin', 'admin', 'facilitator', 'client'],
  'admin': ['admin', 'facilitator', 'client'],
  'facilitator': ['facilitator', 'client'],
  'client': ['client']
};

function canAccessRole(userRole: AppRole | null, allowedRoles: AppRole[]): boolean {
  if (!userRole) return false;

  // Get all roles the user's role can access
  const accessibleRoles = roleHierarchy[userRole] || [];

  // Check if any of the allowed roles are in the accessible roles
  return allowedRoles.some(role => accessibleRoles.includes(role));
}

// Get the default page for a user's role
function getDefaultPageForRole(role: AppRole | null): string {
  switch (role) {
    case 'super_admin':
      return '/super-admin';
    case 'local_admin':
    case 'admin':
      return '/admin';
    case 'facilitator':
      return '/facilitator';
    case 'client':
    default:
      return '/portal';
  }
}

export function RoleBasedRoute({
  children,
  allowedRoles,
  redirectTo
}: RoleBasedRouteProps) {
  const { user, isLoading: authLoading } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();

  if (authLoading || roleLoading) {
    return (
      <LoadingSpinner size="lg" fullPage />
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Use role hierarchy to check access
  if (!canAccessRole(role as AppRole, allowedRoles)) {
    // Redirect to the user's default page based on their role
    const targetPage = redirectTo || getDefaultPageForRole(role as AppRole);
    return <Navigate to={targetPage} replace />;
  }

  return <>{children}</>;
}
